import { UserContentSummary } from '@/domain/usecases/content/user-content-summary'
import { UserContentRecommended } from '@/domain/usecases/content/user-content-recommended'
import { ContentRecommendationType } from '@/domain/models/content/content-recommendation-type'
import { queryClient } from '@/infra/http'
import { importRemote } from '@/main/routes/helpers/loaders'
import { checkFlagEnabled, FeatureFlagName } from '@/infra/feature-flag'
import { idpService } from '@/data/services/idp/idp-service'

/**
 * Loads home page data including content summary and recommendations
 * Recommendations are conditionally loaded based on feature flags
 */
export async function loadHome() {
  const [contentSummary, companyRecommendation, userBasedCFRecommendation, userPDI] =
    await Promise.all([
      getContentSummary(),
      getCompanyRecommendation(),
      getUserBasedCFRecommendation(),
      getUserPDI()
    ])

  return {
    ...contentSummary,
    userPDI,
    companyRecommended: companyRecommendation,
    userBasedCFRecommended: userBasedCFRecommendation
  }
}

async function getContentSummary() {
  const remote = await importRemote<UserContentSummary>('makeRemoteUserContentSummary', queryClient)
  return remote.get()
}

async function getCompanyRecommendation() {
  const recommendationLimit = 3
  const remote = await importRemote<UserContentRecommended>(
    'makeRemoteUserContentRecommended',
    queryClient
  )
  return remote.get(ContentRecommendationType.Enum.COMPANY, recommendationLimit)
}

async function getUserBasedCFRecommendation() {
  const recommendationLimit = 4
  const remote = await importRemote<UserContentRecommended>(
    'makeRemoteUserContentRecommended',
    queryClient
  )
  return remote.get(ContentRecommendationType.Enum.USER_BASED_CF, recommendationLimit)
}

async function getUserPDI() {
  if (!checkFlagEnabled(FeatureFlagName.ENABLE_PDI_RECOMMENDATIONS)) {
    return []
  }
  return idpService.fetchLatestWeakness()
}
